import { ValueAxis } from "@/components/tool-kit/charts/axes";
import { ChartSvg, ChartTitles } from "@/components/tool-kit/charts/chart-svg";
import type { ChartStyle } from "@/components/tool-kit/charts/controls";
import { Marker, px } from "@/components/tool-kit/charts/marks";
import { colorIndex, paletteColor } from "@/lib/tool-kit/charts/color";
import {
  type NumberFormat,
  tickFormat,
  valueText,
} from "@/lib/tool-kit/charts/format";
import {
  CHART_FONT_SIZE,
  CHART_HEIGHT,
  CHART_WIDTH,
  cartesianFrame,
} from "@/lib/tool-kit/charts/layout";
import { linearScale, niceTicks } from "@/lib/tool-kit/charts/scale";
import { maxTextWidth } from "@/lib/tool-kit/charts/text";
import { CHART_INK } from "@/lib/tool-kit/palette";

/*
 * An ogive: the cumulative frequency polygon, with dashed guide lines where quartiles or
 * a percentile were read off it. Each guide runs from the y axis across to the curve and
 * down to the x axis, and its label sits just above the horizontal part at the left,
 * where the curve (still lower) is not in the way. Labels that would collide are spread
 * apart.
 */

const EDGE_LABEL_LIMIT = 16;
const LABEL_GAP = 15;

export interface OgiveGuide {
  /** The text beside the guide: `Q1 = 52.4`. */
  label: string;
  /** Where the curve was read, in data units. */
  x: number;
  /** The height read at, in data units (frequency or percent). */
  y: number;
}

/** Vertical positions for labels, spread so none is closer than `gap` to its neighbour. */
export function spreadLabels(
  ys: readonly number[],
  gap: number,
  top: number
): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  const placed = new Array<number>(ys.length).fill(0);
  let floor = top;
  for (const entry of order) {
    const y = Math.max(entry.y, floor);
    placed[entry.i] = y;
    floor = y + gap;
  }
  return placed;
}

export function OgiveChart({
  id,
  points,
  edges,
  yMax,
  percent,
  guides,
  edgeDecimals,
  style,
  fmt,
  label,
  description,
}: {
  id: string;
  /** (boundary, running total) pairs; y is a frequency, or a percent when `percent`. */
  points: readonly (readonly [number, number])[];
  edges: readonly number[];
  yMax: number;
  percent: boolean;
  guides: readonly OgiveGuide[];
  edgeDecimals: number;
  style: ChartStyle;
  fmt: NumberFormat;
  label: string;
  description: string;
}) {
  const yAxis = niceTicks(0, yMax, { includeZero: true, integer: !percent });
  const yTick = tickFormat(fmt, yAxis.decimals);
  const first = edges[0];
  const last = edges.at(-1) ?? first + 1;
  const xTicks =
    edges.length - 1 <= EDGE_LABEL_LIMIT
      ? [...edges]
      : niceTicks(first, last, { exact: true, count: 8 }).ticks;
  const xTick = tickFormat(fmt, edgeDecimals);
  const frame = cartesianFrame({
    width: CHART_WIDTH,
    height: CHART_HEIGHT,
    title: style.apa ? "" : style.title,
    xTitle: style.xTitle,
    yTitle: style.yTitle,
    leftLabelWidth: maxTextWidth(yAxis.ticks.map(yTick), {
      fontSize: CHART_FONT_SIZE.tick,
    }),
    bottomLabelHeight: CHART_FONT_SIZE.tick,
    topPadding: 10,
  });
  const { plot } = frame;
  const x = linearScale([first, last], [plot.x, plot.x + plot.width]);
  const y = linearScale([yAxis.min, yAxis.max], [plot.y + plot.height, plot.y]);
  const color = paletteColor(colorIndex(style.color), 0);
  const bottom = plot.y + plot.height;
  const line = points
    .map(([px0, py0]) => `${px(x(px0))},${px(y(py0))}`)
    .join(" ");
  const labelYs = spreadLabels(
    guides.map((guide) => y(guide.y) - 5),
    LABEL_GAP,
    plot.y + CHART_FONT_SIZE.value
  );
  const markerSize = points.length > 30 ? 5 : 8;
  return (
    <ChartSvg
      description={description}
      height={frame.height}
      id={id}
      label={label}
      width={frame.width}
    >
      <ChartTitles frame={frame} />
      <ValueAxis
        apa={style.apa}
        format={yTick}
        orient="left"
        plot={plot}
        scale={y}
        ticks={yAxis.ticks}
      />
      <ValueAxis
        apa={style.apa}
        format={xTick}
        grid={false}
        orient="bottom"
        plot={plot}
        scale={x}
        ticks={xTicks}
      />
      <polyline
        fill="none"
        points={line}
        stroke={color}
        strokeLinejoin="round"
        strokeWidth={2.25}
      />
      {guides.map((guide, index) => (
        <g key={`guide-${guide.label}`}>
          <polyline
            fill="none"
            points={`${px(plot.x)},${px(y(guide.y))} ${px(x(guide.x))},${px(y(guide.y))} ${px(x(guide.x))},${px(bottom)}`}
            stroke={CHART_INK.text}
            strokeDasharray="5 3"
            strokeWidth={1.25}
          />
          <text
            fill={CHART_INK.text}
            fontSize={CHART_FONT_SIZE.value + 1}
            fontWeight="bold"
            paintOrder="stroke"
            stroke={CHART_INK.background}
            strokeLinejoin="round"
            strokeWidth={3}
            x={px(plot.x + 6)}
            y={px(labelYs[index])}
          >
            {guide.label}
          </text>
        </g>
      ))}
      {points.map(([pointX, pointY]) => (
        <Marker
          fill={color}
          key={`point-${pointX}`}
          shape="circle"
          size={markerSize}
          x={x(pointX)}
          y={y(pointY)}
        />
      ))}
      {style.valueLabels
        ? points.slice(1).map(([pointX, pointY]) => (
            <text
              fill={CHART_INK.text}
              fontSize={CHART_FONT_SIZE.value}
              key={`value-${pointX}`}
              paintOrder="stroke"
              stroke={CHART_INK.background}
              strokeLinejoin="round"
              strokeWidth={3}
              textAnchor="end"
              x={px(x(pointX) - 5)}
              y={px(y(pointY) - 6)}
            >
              {valueText(fmt, pointY, 1)}
            </text>
          ))
        : null}
    </ChartSvg>
  );
}
